import React, { useRef } from "react";
import { Button, Container, Divider, Stack, Typography } from "@mui/material";
import { Parallax } from "react-parallax";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import WebhookIcon from "@mui/icons-material/Webhook";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import "./styles/homepage.scss";
import ServiceChatBookingForm from "../OurServices/ServiceChatBookingForm";
import { CarouselItemConfig } from "./const/carouselItem";
import HomeCarousel from "./HomeCarousel";

const fadeInUp = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] as const }
  }
};

const Homepage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const servicesRef = useRef(null);
  const techRef = useRef(null);
  const ctaRef = useRef(null);
  const servicesInView = useInView(servicesRef, {
    once: true,
    margin: "-80px"
  });
  const techInView = useInView(techRef, { once: true, margin: "-80px" });
  const ctaInView = useInView(ctaRef, { once: true, margin: "-80px" });

  return (
    <section id="homepageContainer">
      <HomeCarousel
        carouseItemConfig={CarouselItemConfig}
        height={matchMD ? "500px" : "700px"}
      />
      <Parallax
        blur={{ min: -10, max: -1 }}
        strength={100}
        className="parallax-content secondary"
        style={{ padding: 0 }}
      >
        <Stack
          ref={servicesRef}
          justifyContent="center"
          spacing="1.3em"
          alignItems="center"
          flexDirection="column"
          padding="2em 0 0 0"
        >
          <motion.div
            initial="hidden"
            animate={servicesInView ? "visible" : "hidden"}
            variants={fadeInUp}
          >
            <Typography variant="h4" sx={{ color: "var(--color-text)" }}>
              Our Services
            </Typography>
            <Divider textAlign="left">
              <WebhookIcon sx={{ color: "var(--color-primary)" }} />
            </Divider>
          </motion.div>
        </Stack>
        <Stack
          style={{
            padding: "3em 0",
            width: "100%"
          }}
          direction={matchMD ? "column" : "row"}
          justifyContent="center"
          alignItems="center"
        >
          <Stack
            padding="0 5%"
            justifyContent="flex-start"
            alignItems="center"
            flexDirection="column"
            style={{
              width: matchMD ? "85%" : "50%",
              fontWeight: "light",
              height: "80%",
              display: "flex"
            }}
          >
            <motion.p
              initial="hidden"
              animate={servicesInView ? "visible" : "hidden"}
              variants={fadeInUp}
              style={{
                fontSize: "large",
                lineHeight: 1.6,
                color: "var(--color-text-secondary)"
              }}
            >
              At Kivunova, we empower youth by providing tech opportunities,
              mentorship, and real-world projects, paving the way for
              professional success. Our mission extends beyond education, as we
              strive to simplify lives through innovative technology solutions,
              fostering a brighter, more connected future for everyone. Join us
              in transforming potential into achievement and dreams into
              reality.
            </motion.p>
            <motion.section
              initial="hidden"
              animate={servicesInView ? "visible" : "hidden"}
              variants={fadeInUp}
            >
              <Button
                color="primary"
                variant="text"
                onClick={() => navigate("our-services")}
                sx={{ fontWeight: 500 }}
              >
                Read more...
              </Button>
            </motion.section>
          </Stack>
          <Stack
            spacing="2em"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            style={{
              width: matchMD ? "85%" : "50%",
              color: "var(--color-dark)",
              fontWeight: "bold",
              height: "250px",
              overflow: "hidden",
              backdropFilter: "brightness(1)",
              position: "relative"
            }}
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                minWidth: "100%",
                minHeight: "100%",
                width: "100%",
                height: "100%",
                transform: "translate(-50%, -50%)",
                objectFit: "contain",
                zIndex: -1 // Ensures the video stays behind the content
              }}
            >
              <source
                src="https://kivunova.s3.us-east-1.amazonaws.com/kivunova-our-services.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </Stack>
        </Stack>
      </Parallax>
      <Parallax
        blur={{ min: -10, max: -1 }}
        strength={100}
        className="parallax-content secondary"
        style={{ padding: 0 }}
      >
        <Stack
          ref={techRef}
          justifyContent="center"
          spacing="1.3em"
          alignItems="center"
          flexDirection="column"
          padding="2em 0 0 0"
        >
          <motion.div
            initial="hidden"
            animate={techInView ? "visible" : "hidden"}
            variants={fadeInUp}
          >
            <Typography variant="h4" sx={{ color: "var(--color-text)" }}>
              Technologies
            </Typography>
            <Divider textAlign="left">
              <SettingsSuggestIcon sx={{ color: "var(--color-primary)" }} />
            </Divider>
          </motion.div>
        </Stack>
        <Stack
          style={{
            padding: "3em 0",
            width: "100%"
          }}
          direction={matchMD ? "column" : "row"}
          justifyContent="center"
          alignItems="center"
        >
          <Stack
            spacing="2em"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            style={{
              width: matchMD ? "85%" : "50%",
              color: "var(--color-dark)",
              fontWeight: "bold",
              height: "250px",
              overflow: "hidden",
              backdropFilter: "brightness(1)",
              position: "relative"
            }}
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                minWidth: "100%",
                minHeight: "100%",
                width: "100%",
                height: "100%",
                transform: "translate(-50%, -50%)",
                objectFit: "contain",
                zIndex: -1
              }}
            >
              <source
                src="https://kivunova.s3.us-east-1.amazonaws.com/kivunova-technologies.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </Stack>
          <Stack
            padding="0 5%"
            justifyContent="flex-start"
            alignItems="center"
            flexDirection="column"
            style={{
              width: matchMD ? "85%" : "50%",
              fontWeight: "light",
              height: "80%",
              display: "flex"
            }}
          >
            <motion.p
              initial="hidden"
              animate={techInView ? "visible" : "hidden"}
              variants={fadeInUp}
              style={{
                fontSize: "large",
                lineHeight: 1.6,
                color: "var(--color-text-secondary)"
              }}
            >
              At Kivunova, we pride ourselves on leveraging a diverse array of
              cutting-edge technologies to deliver robust and scalable solutions
              for our clients. We specialize in frontend, backend technologies,
              Machine learning and AI to give you a comprehensive view of our
              technical expertise.
            </motion.p>
            <motion.section
              initial="hidden"
              animate={techInView ? "visible" : "hidden"}
              variants={fadeInUp}
            >
              <Button
                color="primary"
                variant="text"
                onClick={() => navigate("technologies")}
                sx={{ fontWeight: 500 }}
              >
                Read more...
              </Button>
            </motion.section>
          </Stack>
        </Stack>
      </Parallax>
      <Stack
        ref={ctaRef}
        minHeight="150px"
        width="100%"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        display="flex"
        spacing="2em"
        padding="4em 0"
      >
        <Container
          sx={{
            width: "90%",
            display: "flex",
            flexDirection: "column",
            gap: "1.5em"
          }}
        >
          <motion.div
            initial="hidden"
            animate={ctaInView ? "visible" : "hidden"}
            variants={fadeInUp}
          >
            <Typography
              variant={matchMD ? "subtitle1" : "h6"}
              textAlign="center"
              fontWeight="bold"
              sx={{ color: "var(--color-text)" }}
            >
              Subscribe to chat with tech team today!
            </Typography>
          </motion.div>
          <motion.div
            initial="hidden"
            animate={ctaInView ? "visible" : "hidden"}
            variants={fadeInUp}
            style={{ transitionDelay: "0.1s" }}
          >
            <Stack>
              <ServiceChatBookingForm />
            </Stack>
          </motion.div>
        </Container>
      </Stack>
    </section>
  );
};

export default Homepage;
